import type { CoachingCard, CoachingCardSkill } from "@/__generated__/api/wrenAPI.schemas";

const SKILL_CONFIG: Record<CoachingCardSkill, { label: string; text: string; bg: string }> = {
  ASK_BETTER: { label: "ASK BETTER", text: "#6b21a8", bg: "#ede9f5" },
  LISTEN_BETTER: { label: "LISTEN BETTER", text: "#3730a3", bg: "#e0e4f5" },
  REPAIR_BETTER: { label: "REPAIR BETTER", text: "#166534", bg: "#dcf5e8" },
};

const AVATAR_COLORS = ["#c27a5a", "#638308"];

export function CoachingCards({ items }: { items: CoachingCard[] }) {
  const uniqueNames = [...new Set(items.map((i) => i.sender_name))];
  const getColorIndex = (name: string) => uniqueNames.indexOf(name);
  const canShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <div className="space-y-6">
      {items.map((item, i) => {
        const colorIdx = getColorIndex(item.sender_name);
        const skill = SKILL_CONFIG[item.skill];
        const isRepair = item.skill === "REPAIR_BETTER";

        return (
          <div
            key={i}
            className="bg-[#fdfcfa] rounded-2xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] overflow-hidden px-6 py-6"
          >
            {/* Header: Avatar + Name | Skill badge */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                  style={{ backgroundColor: AVATAR_COLORS[colorIdx % AVATAR_COLORS.length] }}
                >
                  {item.sender_name[0]?.toUpperCase() || "?"}
                </div>
                <span className="text-base font-semibold text-[#1a1a1a]">
                  {item.sender_name}
                </span>
              </div>
              <span
                className="text-xs font-bold px-2 py-1 rounded-full uppercase tracking-wide"
                style={{ color: skill.text, backgroundColor: skill.bg }}
              >
                {skill.label}
              </span>
            </div>

            {/* Explanation */}
            <p className="text-base text-[#555] leading-relaxed mb-4">
              {item.explanation}
            </p>

            {/* Quote box — subtle gray, no border accent, no label */}
            <div className="bg-[#f5f3ef] rounded-xl px-4 py-4">
              <p className="text-base text-[#1a1a1a] leading-relaxed italic">
                &ldquo;{item.reframed_message}&rdquo;
              </p>
            </div>

            {/* Share — only on REPAIR_BETTER cards */}
            {isRepair && (
              <div className="border-t border-[#e5e3df] mt-4 pt-4 flex items-center justify-between">
                <span className="text-sm text-[#bbb]">Send to your partner</span>
                <button
                  onClick={() => canShare && navigator.share({ text: item.reframed_message })}
                  className="flex items-center gap-2 text-sm font-medium text-[#1a1a1a] bg-[#f0eeea] px-4 py-2 rounded-full"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 12V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V12" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                  Share
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
